import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  getTickets,
  assignTicket,
  bulkUpdateTickets,
  getSupportStats
} from '../../services/api';
import {
  FiEye,
  FiUser,
  FiUserPlus,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiPlus,
  FiAlertCircle,
  FiCheckCircle,
  FiMessageSquare,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
  FiHelpCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiMoreVertical,
  FiTag,
  FiCalendar,
  FiActivity,
  FiShield,
  FiMail,
  FiStar,
  FiAward,
  FiPackage,
  FiX,
  FiCheck,
  FiGrid,
  FiUsers
} from 'react-icons/fi';

const SupportTickets = ({ admin }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category_id: '',
    assigned_to: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [viewMode, setViewMode] = useState('table'); // table or card

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getTickets(filters);
      
      if (response.success && response.data) {
        setTickets(response.data.tickets || []);
        setPagination(response.data.pagination || {});
      } else {
        setTickets(response.tickets || response.data || []);
        setPagination(response.pagination || {});
      }
    } catch (err) {
      setError('Failed to fetch tickets: ' + err.message);
      console.error('Tickets fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getSupportStats();
      
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setStats(response);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/admin/support/tickets/${ticketId}`);
  };

  const handleAssignToMe = async (ticketId) => {
    try {
      await assignTicket(ticketId, { assigned_to: admin.id });
      setSuccessMessage('Ticket assigned to you successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTickets();
    } catch (err) {
      setError('Failed to assign ticket: ' + err.message);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedTickets.length === 0) return;

    try {
      const data = {
        ticket_ids: selectedTickets,
        action: bulkAction,
        value: bulkValue
      };

      const response = await bulkUpdateTickets(data);
      setSuccessMessage(`Updated ${response.success_count || selectedTickets.length} tickets successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      setSelectedTickets([]);
      setShowBulkModal(false);
      setBulkAction('');
      setBulkValue('');
      
      fetchTickets();
    } catch (err) {
      setError('Failed to perform bulk action: ' + err.message);
    }
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map(t => t.ticket_id));
    }
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category_id: '',
      assigned_to: '',
      search: '',
      page: 1,
      limit: 20
    });
    setShowFilters(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'open': { color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-800/50', icon: <FiAlertCircle />, label: 'Open' },
      'in_progress': { color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-800/50', icon: <FiActivity />, label: 'In Progress' },
      'awaiting_user': { color: 'text-purple-400', bg: 'bg-purple-900/30', border: 'border-purple-800/50', icon: <FiUser />, label: 'Awaiting User' },
      'awaiting_admin': { color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-800/50', icon: <FiClock />, label: 'Awaiting Admin' },
      'resolved': { color: 'text-green-400', bg: 'bg-green-900/30', border: 'border-green-800/50', icon: <FiCheckCircle />, label: 'Resolved' },
      'closed': { color: 'text-gray-400', bg: 'bg-gray-700', border: 'border-gray-600', icon: <FiShield />, label: 'Closed' }
    };
    return configs[status] || configs.closed;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      'urgent': { color: 'text-red-400', bg: 'bg-red-900/30', border: 'border-red-800/50', icon: <FiAlertCircle /> },
      'high': { color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-800/50', icon: <FiTrendingUp /> },
      'medium': { color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-800/50', icon: <FiBarChart2 /> },
      'low': { color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-800/50', icon: <FiTrendingDown /> }
    };
    return configs[priority] || { color: 'text-gray-400', bg: 'bg-gray-700', border: 'border-gray-600', icon: <FiHelpCircle /> };
  };

  // Loading Skeleton
  if (loading && tickets.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Support</span> Tickets
            </h1>
            <p className="text-gray-400 mt-1">Manage and monitor customer support requests</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => fetchTickets()}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={() => {/* Export functionality */}}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
            
            <Link
              to="/admin/support/tickets/new"
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiPlus className="mr-2" />
              New Ticket
            </Link>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-blue-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Total Tickets</p>
                <p className="text-2xl font-bold text-white mt-1 group-hover:text-blue-300 transition-colors">
                  {stats.stats?.total_tickets || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  All time
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FiMessageSquare className="text-blue-400 text-2xl" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-yellow-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Open Tickets</p>
                <p className="text-2xl font-bold text-white mt-1 group-hover:text-yellow-300 transition-colors">
                  {stats.stats?.open_tickets || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Active support requests
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FiAlertCircle className="text-yellow-400 text-2xl" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-red-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Unassigned</p>
                <p className="text-2xl font-bold text-white mt-1 group-hover:text-red-300 transition-colors">
                  {stats.stats?.unassigned_tickets || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Needs agent assignment
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FiUsers className="text-red-400 text-2xl" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-purple-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">Last 24h</p>
                <p className="text-2xl font-bold text-white mt-1 group-hover:text-purple-300 transition-colors">
                  {stats.stats?.tickets_last_24h || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Recent activity
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FiClock className="text-purple-400 text-2xl" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters & Search</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_user">Awaiting User</option>
                  <option value="awaiting_admin">Awaiting Admin</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Assignment Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assignment</label>
                <select
                  value={filters.assigned_to}
                  onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All</option>
                  <option value="unassigned">Unassigned</option>
                  <option value={admin.id}>Assigned to Me</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowFilters(false);
                    fetchTickets();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedTickets.length > 0 && (
        <div className="mb-6 bg-gray-800 rounded-xl border border-gray-700 p-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiPackage className="text-[#ff660e]" />
              </div>
              <div>
                <h3 className="font-medium text-white">Bulk Actions</h3>
                <p className="text-sm text-gray-400">
                  {selectedTickets.length} ticket{selectedTickets.length === 1 ? '' : 's'} selected
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={bulkAction}
                onChange={(e) => {
                  setBulkAction(e.target.value);
                  if (e.target.value) {
                    setShowBulkModal(true);
                  }
                }}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              >
                <option value="">Choose Action</option>
                <option value="assign">Assign</option>
                <option value="status">Change Status</option>
                <option value="priority">Change Priority</option>
              </select>
              <button
                onClick={() => setSelectedTickets([])}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-400">
          {loading ? 'Loading...' : `Showing ${tickets.length} tickets`}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'table'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiBarChart2 />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'card'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {/* Tickets Content */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">
                    <input
                      type="checkbox"
                      checked={tickets.length > 0 && selectedTickets.length === tickets.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Ticket Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {tickets.length > 0 ? (
                  tickets.map((ticket) => {
                    const statusConfig = getStatusConfig(ticket.status);
                    const priorityConfig = getPriorityConfig(ticket.priority);
                    
                    return (
                      <tr key={ticket.ticket_id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedTickets.includes(ticket.ticket_id)}
                            onChange={() => toggleTicketSelection(ticket.ticket_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-semibold text-white">
                              #{ticket.ticket_id} - {ticket.title}
                            </div>
                            {ticket.description && (
                              <div className="text-xs text-gray-500 mt-1 truncate max-w-md">
                                {ticket.description.length > 100 
                                  ? ticket.description.substring(0, 100) + '...' 
                                  : ticket.description}
                              </div>
                            )}
                            <div className="mt-1">
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-400">
                                {ticket.category_name || 'General'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-300">
                                {ticket.user_first_name?.[0]}{ticket.user_last_name?.[0]}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {ticket.user_first_name} {ticket.user_last_name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{ticket.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
                            {statusConfig.icon}
                            <span className="ml-1">{statusConfig.label}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${priorityConfig.bg} ${priorityConfig.color} ${priorityConfig.border}`}>
                            {priorityConfig.icon}
                            <span className="ml-1">
                              {ticket.priority?.toUpperCase()}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-400">
                            {formatDate(ticket.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewTicket(ticket.ticket_id)}
                              className="px-3 py-1 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center"
                            >
                              <FiEye className="mr-1" />
                              View
                            </button>
                            {!ticket.assigned_to && (
                              <button
                                onClick={() => handleAssignToMe(ticket.ticket_id)}
                                className="px-3 py-1 bg-[#ff660e]/20 hover:bg-[#ff660e]/30 text-[#ff660e] rounded-lg text-sm font-medium transition-colors flex items-center"
                              >
                                <FiUserPlus className="mr-1" />
                                Assign
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-600 text-6xl mb-6">📭</div>
                      <h3 className="text-xl font-medium text-white mb-2">No tickets found</h3>
                      <p className="text-gray-400 mb-6">Try adjusting your search or create the first ticket</p>
                      <Link
                        to="/admin/support/tickets/new"
                        className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                      >
                        <FiPlus className="mr-2" />
                        Create First Ticket
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div className="p-6">
            {tickets.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tickets.map((ticket) => {
                  const statusConfig = getStatusConfig(ticket.status);
                  const priorityConfig = getPriorityConfig(ticket.priority);
                  
                  return (
                    <div key={ticket.ticket_id} className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedTickets.includes(ticket.ticket_id)}
                            onChange={() => toggleTicketSelection(ticket.ticket_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                          <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                            <FiHelpCircle className="text-gray-400" />
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${priorityConfig.bg} ${priorityConfig.color}`}>
                          {ticket.priority?.toUpperCase()}
                        </span>
                      </div>
                      
                      <h4 className="font-medium text-white mb-2 group-hover:text-[#ff660e] transition-colors">
                        #{ticket.ticket_id} - {ticket.title}
                      </h4>
                      
                      {ticket.description && (
                        <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                          {ticket.description.substring(0, 100)}...
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-300">
                            {ticket.user_first_name?.[0]}
                          </span>
                        </div>
                        <span className="text-sm text-gray-300">
                          {ticket.user_first_name} {ticket.user_last_name}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between mb-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                          {statusConfig.icon}
                          <span className="ml-1">{statusConfig.label}</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex space-x-2 pt-4 border-t border-gray-600">
                        <button
                          onClick={() => handleViewTicket(ticket.ticket_id)}
                          className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                        {!ticket.assigned_to && (
                          <button
                            onClick={() => handleAssignToMe(ticket.ticket_id)}
                            className="flex-1 px-3 py-1.5 bg-[#ff660e]/20 hover:bg-[#ff660e]/30 text-[#ff660e] rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                          >
                            <FiUserPlus className="mr-1" />
                            Assign
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-600 text-6xl mb-6">📭</div>
                <h3 className="text-xl font-medium text-white mb-2">No tickets found</h3>
                <p className="text-gray-400 mb-6">Try adjusting your search or create the first ticket</p>
                <Link
                  to="/admin/support/tickets/new"
                  className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                >
                  <FiPlus className="mr-2" />
                  Create First Ticket
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {tickets.length > 0 && pagination && (
          <div className="px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-400">
              Showing {((pagination.page || 1) - 1) * (pagination.limit || 20) + 1} to {Math.min((pagination.page || 1) * (pagination.limit || 20), pagination.total || 0)} of {pagination.total || 0} tickets
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) <= 1}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="text-gray-300" />
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(Math.min(5, pagination.pages || 1)).keys()].map(i => {
                  const pageNum = i + 1;
                  const isCurrent = pageNum === (pagination.page || 1);
                  
                  let showPage = false;
                  if ((pagination.pages || 1) <= 5) {
                    showPage = true;
                  } else if (pageNum <= 2 || pageNum >= (pagination.pages || 1) - 1 || Math.abs(pageNum - (pagination.page || 1)) <= 1) {
                    showPage = true;
                  }
                  
                  if (showPage) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleFilterChange('page', pageNum)}
                        className={`w-8 h-8 rounded-lg transition-colors ${
                          isCurrent 
                            ? 'bg-[#ff660e] text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === 3 && (pagination.pages || 1) > 5) {
                    return <span key="ellipsis" className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.pages || 1)}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="text-gray-300" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Bulk {bulkAction.charAt(0).toUpperCase() + bulkAction.slice(1)}
                </h3>
                <button
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkAction('');
                    setBulkValue('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {bulkAction === 'assign' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Assign tickets to:
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setBulkValue(admin.id)}
                      className={`w-full px-4 py-3 rounded-lg text-left transition-colors flex items-center justify-between ${
                        bulkValue === admin.id 
                          ? 'bg-[#ff660e]/20 border border-[#ff660e]/30' 
                          : 'bg-gray-700 hover:bg-gray-600 border border-gray-600'
                      }`}
                    >
                      <div className="flex items-center">
                        <FiUser className="mr-2 text-[#ff660e]" />
                        <div>
                          <div className="font-medium text-white">Assign to Me</div>
                          <div className="text-sm text-gray-400">Take ownership of tickets</div>
                        </div>
                      </div>
                      {bulkValue === admin.id && <FiCheck className="text-[#ff660e]" />}
                    </button>
                    <div className="text-center text-sm text-gray-400">or</div>
                    <input
                      type="text"
                      className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white placeholder-gray-500"
                      placeholder="Enter Admin ID"
                      value={bulkValue === admin.id ? '' : bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      disabled={bulkValue === admin.id}
                    />
                  </div>
                </div>
              )}

              {bulkAction === 'status' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Change status to:
                  </label>
                  <select
                    className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="awaiting_user">Awaiting User</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              )}

              {bulkAction === 'priority' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Change priority to:
                  </label>
                  <select
                    className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                  >
                    <option value="">Select Priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              )}
            </div>

            <div className="p-6 pt-0 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkAction('');
                  setBulkValue('');
                }}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAction}
                disabled={!bulkValue}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply to {selectedTickets.length} Tickets
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default SupportTickets;